import { useCallback } from 'react';

import { graphql, useMutation } from 'react-relay';

import type { TrackType } from '../../api/cc-smarts/models/TrackType';

import type { useRemoveTrackMutation } from './__generated__/useRemoveTrackMutation.graphql';

export const useRemoveTrack = () => {
	const [commitMutation, isMutationInFlight] = useMutation<useRemoveTrackMutation>(graphql`
		mutation useRemoveTrackMutation($cloudId: ID!, $input: ConfluenceTrackInput!) {
			confluence_removeTrack(cloudId: $cloudId, input: $input) {
				success
				errors {
					message
					extensions {
						statusCode
					}
				}
			}
		}
	`);

	const removeTrack = useCallback(
		(cloudId: string, mainSource: string, supportingSources: string[], trackType: TrackType) => {
			return new Promise<boolean>((resolve, reject) => {
				commitMutation({
					variables: {
						cloudId,
						input: {
							mainSource,
							supportingSources,
							trackType,
						},
					},
					onCompleted: (response) => {
						const success = response.confluence_removeTrack?.success;
						const errors = response.confluence_removeTrack?.errors;

						if (success) {
							resolve(true);
						} else {
							const errorMessage = errors?.[0]?.message || 'Failed to remove track';
							reject(new Error(errorMessage));
						}
					},
					onError: (error) => {
						reject(error);
					},
					updater: (store) => {
						// Optionally update the Relay store to remove the track from the cache
						// This ensures the UI updates immediately without refetching
						const playlistRecord = store.getRoot().getLinkedRecord('confluence_getPlaylist', {
							cloudId,
						});
						if (playlistRecord) {
							const tracksRecord = playlistRecord.getLinkedRecord('tracks');
							if (tracksRecord) {
								const nodes = tracksRecord.getLinkedRecords('nodes');
								if (nodes) {
									const updatedNodes = nodes.filter((node) => {
										const mainContent = node?.getLinkedRecord('mainContent');
										const mainContentId = mainContent?.getValue('id');
										return mainContentId !== mainSource.split('/').pop();
									});
									tracksRecord.setLinkedRecords(updatedNodes, 'nodes');
								}
							}
						}
					},
				});
			});
		},
		[commitMutation],
	);

	return {
		removeTrack,
		isRemoving: isMutationInFlight,
	};
};
